/**
 * Shared Application Constants
 * System-wide constants for roles, statuses, HTTP codes, etc.
 * @module @shared/constants
 */

/**
 * User roles in the system
 * - ADMIN: administrator with access to the admin portal
 * - USER: agent or standard user with access to the agent portal
 */
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  USER: 'USER',
} as const;

/**
 * Role strings as they appear in token claims (from FusionAuth)
 */
export const USER_ROLE_CLAIMS = {
  ADMIN: 'admin',
  USER: 'user',
} as const;

/**
 * HTTP Status codes
 */
export const HTTP_STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
} as const;

/**
 * Application route paths
 */
export const APP_ROUTES = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  NOT_FOUND: '/not-found',
  FORBIDDEN: '/forbidden',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ORG_DASHBOARD: '/org/dashboard',
  USER_DASHBOARD: '/user/dashboard',
} as const;

/**
 * Entity statuses
 */
export const ENTITY_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PAUSED: 'paused',
} as const;

/** Resolved status code of a user account. */
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;
export type UserStatusCode = (typeof USER_STATUS)[keyof typeof USER_STATUS];

/**
 * Difficulty levels
 */
export const DIFFICULTY_LEVELS = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard',
} as const;

/**
 * Sentiment types
 */
export const SENTIMENT_TYPES = {
  POSITIVE: 'positive',
  NEUTRAL: 'neutral',
  NEGATIVE: 'negative',
} as const;

/**
 * Validation patterns
 */
export const VALIDATION_PATTERNS = {
  /**
   * Email validation regex
   */
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  /**
   * URL validation regex
   */
  URL: /^https?:\/\/.+/,
} as const;

/**
 * Currency-related constants
 */
/** Earliest date a date-range filter accepts; a two-digit year typed by hand stays out. */
export const DATE_RANGE_MIN = '2000-01-01';

export const CURRENCY = {
  DEFAULT: 'VND',
  SUPPORTED: ['VND', 'USD', 'EUR', 'JPY', 'GBP', 'AUD', 'CAD'],
} as const;
